import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Calendar, ArrowLeft, Tag } from "lucide-react";
import HeaderV2 from "./HeaderV2";
import FooterV2 from "./FooterV2";
import CTAV2 from "./sections/CTAV2";
import useSmoothScroll from "../../hooks/useSmoothScroll";
import { getPostBySlug } from "../../lib/api";
import { categoryLabel } from "../../lib/categories";

function fmt(d) {
  if (!d) return "";
  return new Date(d).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function setMeta(name, content, attr = "name") {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function BlogPostV2() {
  useSmoothScroll(true);
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    getPostBySlug(slug)
      .then((p) => {
        setPost(p);
        // SEO: update document title + meta
        document.title = (p.meta_title || p.title) + " — StartINC";
        setMeta("description", p.meta_description || p.excerpt || "");
        setMeta("og:title", p.meta_title || p.title, "property");
        setMeta("og:description", p.meta_description || p.excerpt || "", "property");
        if (p.cover_image) setMeta("og:image", p.cover_image, "property");
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        else navigate("/blog", { replace: true });
      });
    return () => {
      document.title = "StartINC — UAE Business Setup, Visa & Corporate Banking";
    };
  }, [slug, navigate]);

  if (notFound) {
    return (
      <div className="bg-white">
        <HeaderV2 />
        <main className="container-x py-32 text-center">
          <p className="overline text-[#3366FF]">404</p>
          <h1 className="mt-3 font-heading font-light text-3xl tracking-tight text-[#0F172A]">
            Post not found.
          </h1>
          <p className="mt-3 text-slate-600">
            It may have been removed or never published.
          </p>
          <Link to="/blog" className="btn-gold inline-flex mt-7">
            Back to blog
          </Link>
        </main>
        <FooterV2 />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="bg-white">
        <HeaderV2 />
        <main className="container-x py-32 text-slate-400">Loading…</main>
        <FooterV2 />
      </div>
    );
  }

  return (
    <div data-testid="v2-blog-post" className="bg-white">
      <HeaderV2 />
      <main>
        {/* Hero */}
        <section className="relative bg-[#001A4D] text-white pt-28 pb-12 md:pt-32 md:pb-16 overflow-hidden">
          {post.cover_image && (
            <>
              <div className="absolute inset-0 -z-20">
                <img
                  src={post.cover_image}
                  alt=""
                  className="w-full h-full object-cover opacity-35"
                />
              </div>
              <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#001A4D]/80 via-[#002366]/85 to-[#001A4D]" />
            </>
          )}
          <div className="container-x relative">
            <Link
              to="/blog"
              className="inline-flex items-center gap-2 text-sm text-slate-300 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4" /> All articles
            </Link>
            <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs uppercase tracking-widest font-heading text-slate-300">
              <span className="inline-flex items-center gap-1.5">
                <Calendar className="w-3.5 h-3.5" />
                {fmt(post.published_at || post.created_at)}
              </span>
              <span className="inline-flex items-center gap-1.5 text-[#6B8AFF]">
                <Tag className="w-3.5 h-3.5" />
                {categoryLabel(post.category)}
              </span>
            </div>
            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="mt-5 font-heading font-light tracking-tight text-3xl sm:text-4xl lg:text-5xl leading-[1.1] max-w-4xl"
            >
              {post.title}
            </motion.h1>
            {post.excerpt && (
              <motion.p
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="mt-5 text-slate-200/85 max-w-3xl leading-relaxed"
              >
                {post.excerpt}
              </motion.p>
            )}
          </div>
        </section>

        {/* Body */}
        <section className="bg-white py-12 md:py-16">
          <div className="container-x max-w-3xl">
            <article
              data-testid="blog-post-body"
              className="prose prose-slate prose-lg max-w-none prose-headings:font-heading prose-headings:font-semibold prose-headings:text-[#0F172A] prose-h2:text-3xl prose-h2:mt-12 prose-h3:text-xl prose-a:text-[#3366FF] prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-[#3366FF] prose-blockquote:text-slate-600 prose-img:rounded-sm prose-strong:text-[#0F172A]"
            >
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {post.content || ""}
              </ReactMarkdown>
            </article>
          </div>
        </section>

        <CTAV2 />
      </main>
      <FooterV2 />
    </div>
  );
}
